// Multivariate Dependencies
import React, { useCallback } from 'react';
// Components
import { Container } from '../../../common/styles/container/styles/Container';
import { DropdownWithPrefixedOptions } from './DropdownWithPrefixedOptions';
import { TooltipLegacy } from '../../../legacy/TooltipLegacy';
/**
 * A wrapper component that combines both the tooltip and dropdown as one.
 *
 * @example
 *  <DropdownWithTooltip
 *    // Forces the state of the dropdown, in this scenario, the dropdown will render as 'locked'
 *    initialDropdownState={DropdownStates.LOCKED}
 *
 *    // Populates the dropdown with a single value that is selected. This will also be added to the dropdown options
 *    // so ensure the icon is set (otherwise you will see this option in the menu without the icon)
 *    initalDropdownOption={{
 *      id: 1,
 *      label: 'Initial value',
 *      icon: <FaRocket />,
 *      payload: {
 *        unique: 'data'
 *      }
 *    }}
 *
 *    // Callback when a dropdown option is selected
 *    onDropdownOptionSelected={(dropdownOption: DropdownOption) => doSomething(dropdownOption)}
 *
 *    // Callback when the dropdown is cleared (the X icon is clicked, backspace or delete is pressed on the text input field)
 *    onDropdownOptionCleared={() => doSomething()}
 *
 *    // Called when loading the dropdown menu.
 *    // Allow the user to fetch data and correctly format the payload to be loaded in the dropdown.
 *    // Data MUST contain an id and label. The icon is option. Payload can be anything and will be accessible in onDropdownOptionSelected
 *    getDropdownOptions={(searchValue: string) => doHttpRequestAndFormatResponse(searchValue)}
 *
 *    // Called when the tooltip is ready to open.
 *    // Returning JSX will render the JSX to the tooltip container, If nothing is returned then the tooltip will not render
 *    getTooltipContent={(dropdownOption?: DropdownOption) => <div>My tooltip</div>}
 *
 *    // Callback when the enter key is pressed while the focus is on the dropdown text input field AND the dropdown menu is closed
 *    handleEnterOnInput={() => doSomething()}
 *
 *    // Callback when the user has double clicked on the dropdown text input field. Can be used to open a new tab with the dropdown option data
 *    handleDoubleClickOnInput={(dropdownOption?: DropdownOption) => doSomething(dropdownOption)}
 *
 *    // If this is set to anything other than null or undefined, the entire dropdown will render with error colours
 *    // This property is to be handled by the user. Dropdown will never automatically remove errors
 *    errorMessage='This is an optional error'
 *  />
 * @param {DropdownProps} {
 *   onDropdownOptionSelected,
 *   onDropdownOptionCleared,
 *   getDropdownOptions,
 *   getTooltipContent,
 *   handleEnterOnInput,
 *   handleDoubleClickOnInput,
 * }
 * @return {JSX.Element}
 */
export var DropdownWithPrefixedOptionsAndTooltip = function (_a) {
    var _b = _a.showTooltipDelay, showTooltipDelay = _b === void 0 ? 400 : _b, initialDropdownState = _a.initialDropdownState, initialDropdownOption = _a.initialDropdownOption, onDropdownOptionSelected = _a.onDropdownOptionSelected, onDropdownOptionCleared = _a.onDropdownOptionCleared, getTooltipContent = _a.getTooltipContent, getDropdownOptions = _a.getDropdownOptions, handleEnterOnInput = _a.handleEnterOnInput, handleDoubleClickOnInput = _a.handleDoubleClickOnInput, errorMessage = _a.errorMessage;
    var _c = React.useState(), tooltipEventElement = _c[0], setTooltipEventElement = _c[1];
    var tooltipEventCallback = useCallback(function (ref) {
        ref && setTooltipEventElement(ref);
    }, []);
    return (React.createElement(Container, { display: 'block', ref: tooltipEventCallback },
        React.createElement(TooltipLegacy, { showTooltipDelay: showTooltipDelay, publishedTooltipEventElement: tooltipEventElement }),
        React.createElement(DropdownWithPrefixedOptions, { initialDropdownState: initialDropdownState, initialDropdownOption: initialDropdownOption, subscribedTooltipEventElement: tooltipEventElement, onDropdownOptionSelected: onDropdownOptionSelected, onDropdownOptionCleared: onDropdownOptionCleared, getDropdownOptions: getDropdownOptions, getTooltipContent: getTooltipContent, handleEnterOnInput: handleEnterOnInput, handleDoubleClickOnInput: handleDoubleClickOnInput, errorMessage: errorMessage })));
};
//# sourceMappingURL=DropdownWithPrefixedOptionsAndTooltip.js.map